import type { UserProvider, UserProviderCapability } from "@kontextwerk/control-web-client"
export type {
  UserProviderAccountsOperations,
  UserProviderAccountsSurfaceProps,
  UserProviderCapabilityContext,
} from "./types"

const CANONICAL_PARAMS = {
  provider: "provider_account_provider",
  intent: "provider_account_intent",
  result: "provider_account_result",
  error: "provider_account_error",
  capability: "provider_account_capability",
} as const

export type UserProviderAccountOAuthResult = "success" | "cancelled" | "error"

export type UserProviderAccountOAuthCallback = {
  provider: UserProvider
  result: UserProviderAccountOAuthResult | null
  error: string | null
  requestedCapability: UserProviderCapability | null
}

type BrowserLocation = Pick<Location, "pathname" | "search" | "hash">

const paramsFrom = (search: string | null | undefined): URLSearchParams =>
  new URLSearchParams((search ?? "").replace(/^\?/, ""))

const providerFrom = (value: string | null): UserProvider | null => {
  const normalized = value?.trim().toLowerCase()

  if (normalized === "google" || normalized === "microsoft") return normalized

  return null
}

const resultFrom = (value: string | null): UserProviderAccountOAuthResult | null => {
  const normalized = value?.trim().toLowerCase()

  if (normalized === "success" || normalized === "cancelled" || normalized === "error")
    return normalized

  return null
}

const capabilityFrom = (value: string | null): UserProviderCapability | null => {
  const normalized = value?.trim().toLowerCase()

  if (normalized === "calendar" || normalized === "mail_send") return normalized

  return null
}

const normalizedPathname = (pathname: string): string => {
  const value = pathname.trim() || "/"

  return value.startsWith("/") ? value : `/${value}`
}

const normalizedHash = (hash: string): string => {
  if (!hash) return ""

  return hash.startsWith("#") ? hash : `#${hash}`
}

const buildLocation = (location: BrowserLocation, params: URLSearchParams): string => {
  const search = params.toString()

  return `${normalizedPathname(location.pathname)}${search ? `?${search}` : ""}${normalizedHash(location.hash)}`
}

export const parseUserProviderAccountOAuthCallback = (
  search: string | null | undefined,
): UserProviderAccountOAuthCallback | null => {
  const params = paramsFrom(search)
  const provider = providerFrom(params.get(CANONICAL_PARAMS.provider))

  if (!provider || params.get(CANONICAL_PARAMS.intent) !== "oauth_callback") return null

  return {
    provider,
    result: resultFrom(params.get(CANONICAL_PARAMS.result)),
    error: params.get(CANONICAL_PARAMS.error)?.trim() || null,
    requestedCapability: capabilityFrom(params.get(CANONICAL_PARAMS.capability)),
  }
}

export const buildUserProviderAccountCapabilityReturnPath = (
  returnPath: string,
  capability: UserProviderCapability,
): string => {
  const parsed = new URL(returnPath, "https://control.invalid")
  parsed.searchParams.set(CANONICAL_PARAMS.capability, capability)

  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

export const buildUserProviderAccountOAuthReturnPath = (
  location: BrowserLocation,
  provider: UserProvider,
): string => {
  const params = paramsFrom(location.search)
  params.set(CANONICAL_PARAMS.provider, provider)
  params.set(CANONICAL_PARAMS.intent, "oauth_callback")
  params.delete(CANONICAL_PARAMS.result)
  params.delete(CANONICAL_PARAMS.error)
  params.delete(CANONICAL_PARAMS.capability)

  return buildLocation(location, params)
}

export const clearUserProviderAccountOAuthCallbackParams = (location: BrowserLocation): string => {
  const params = paramsFrom(location.search)
  for (const key of Object.values(CANONICAL_PARAMS)) params.delete(key)

  return buildLocation(location, params)
}

export const describeUserProviderAccountOAuthError = (
  provider: UserProvider,
  error: string | null,
): string => {
  const providerLabel = provider === "google" ? "Google" : "Microsoft"

  if (error === "access_denied" || error === "cancelled") {
    return `${providerLabel}-Verbindung wurde abgebrochen. Es wurden keine Änderungen gespeichert.`
  }

  if (
    error === "state_expired" ||
    error === "state_invalid" ||
    error === "state_replayed" ||
    error === "authorization_code_missing"
  ) {
    return `Die ${providerLabel}-Verbindung ist abgelaufen. Bitte starten Sie die Verbindung erneut.`
  }

  if (error === "account_already_connected") {
    return `Dieses ${providerLabel}-Konto ist bereits mit einem anderen Benutzer verbunden.`
  }

  if (error === "different_account_requires_disconnect") {
    return `Trennen Sie zuerst das bisherige ${providerLabel}-Konto, bevor Sie ein anderes Konto verbinden.`
  }

  if (error === "active_provider_conflict") {
    return "Für diesen Benutzer besteht bereits eine andere Kalenderverbindung. Trennen Sie diese zuerst. Die E-Mail-Verbindung kann bestehen bleiben."
  }

  if (error === "active_mail_provider_conflict") {
    return "Für diesen Benutzer besteht bereits eine andere E-Mail-Verbindung. Trennen Sie diese zuerst. Die Kalenderverbindung kann bestehen bleiben."
  }

  if (error === "account_kind_mismatch" || error === "provider_account_kind_invalid") {
    return `Der ausgewählte ${providerLabel}-Kontotyp passt nicht zum verbundenen Konto. Bitte starten Sie die Verbindung mit dem passenden Kontotyp erneut.`
  }

  if (
    error === "permission_required" ||
    error === "provider_scope_missing" ||
    error === "provider_refresh_token_missing"
  ) {
    return `${providerLabel} hat nicht alle benötigten Berechtigungen erteilt. Bitte verbinden Sie das Konto erneut.`
  }

  if (error === "conditional_access_required") {
    return `${providerLabel} verlangt eine zusätzliche Anmeldung oder Freigabe. Bitte schließen Sie diese bei ${providerLabel} ab und versuchen Sie es erneut.`
  }

  if (error === "provider_identity_invalid" || error === "identity_validation_failed") {
    return `Das ${providerLabel}-Konto konnte nicht sicher bestätigt werden. Bitte starten Sie die Verbindung erneut.`
  }

  if (error === "provider_reauth_required") {
    return `Die Anmeldung bei ${providerLabel} ist nicht mehr gültig. Bitte verbinden Sie das Konto erneut.`
  }

  if (error === "provider_disconnect_in_progress") {
    return `Das ${providerLabel}-Konto wird noch getrennt. Bitte warten Sie kurz und versuchen Sie es danach erneut.`
  }

  if (
    error === "oauth_exchange_failed" ||
    error === "provider_oauth_failed" ||
    error === "provider_temporarily_unavailable"
  ) {
    return `${providerLabel} ist vorübergehend nicht erreichbar. Bitte versuchen Sie die Verbindung später erneut.`
  }

  if (
    error === "provider_not_configured" ||
    error === "provider_endpoint_invalid" ||
    error === "provider_scope_configuration_invalid" ||
    error === "provider_credential_keyring_invalid" ||
    error === "provider_credential_keyring_unavailable" ||
    error === "provider_settings_unavailable" ||
    error === "control_admin_origin_unavailable"
  ) {
    return `Die ${providerLabel}-Verbindung ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.`
  }

  if (error === "provider_account_activation_failed") {
    return `Das ${providerLabel}-Konto wurde autorisiert, aber die Verbindung konnte nicht aktiviert werden. Bitte versuchen Sie es erneut.`
  }

  return `Die ${providerLabel}-Verbindung konnte nicht sicher abgeschlossen werden. Bitte versuchen Sie es erneut.`
}
